import React, { memo, useState, useEffect } from 'react';
import styles from './DesktopAppBanner.module.css';
import useSystemInfo from '@hooks/useSystemInfo';
import { useTranslation } from '@hooks/index';
import getHubDownloadUrl from '@common/utils/getHubDownloadUrl';
import { useHubContext } from '@common/context/HubContext/HubContext';

const BANNER_DISMISSED_KEY = 'hub-banner-dismissed';
const DISMISSAL_EXPIRATION_MS = 15 * 60 * 1000; // 15 minutes in milliseconds

/** The height of the banner in pixels (includes margin-bottom) */
export const DESKTOP_APP_BANNER_HEIGHT = 76; // 60px height + 16px margin-bottom

type DesktopAppBannerProps = {
  /** Callback invoked when the banner visibility changes */
  onVisibilityChange?: (visible: boolean) => void;
};

const DesktopAppBanner = ({ onVisibilityChange }: DesktopAppBannerProps) => {
  const { online } = useHubContext();
  const { platform, architecture } = useSystemInfo();
  const [dismissed, setDismissed] = useState(false);
  const t = useTranslation('DesktopAppBanner');

  useEffect(() => {
    // Check if user has previously dismissed the banner
    const dismissedData = localStorage.getItem(BANNER_DISMISSED_KEY);

    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const currentTime = Date.now();
        const elapsedTime = currentTime - timestamp;

        // Only consider the banner dismissed if less than 15 minutes have passed
        if (elapsedTime < DISMISSAL_EXPIRATION_MS) {
          setDismissed(true);
        } else {
          // Clear expired dismissal data
          localStorage.removeItem(BANNER_DISMISSED_KEY);
        }
      } catch (e) {
        // If stored data is invalid, remove it
        localStorage.removeItem(BANNER_DISMISSED_KEY);
      }
    }
  }, []);

  const handleDismiss = () => {
    // Store dismissal time
    const dismissalData = {
      timestamp: Date.now(),
    };
    localStorage.setItem(BANNER_DISMISSED_KEY, JSON.stringify(dismissalData));
    setDismissed(true);
  };

  const isInvisible =
    online || !platform || !architecture || dismissed;

  // Notify parent component when visibility changes
  useEffect(() => {
    onVisibilityChange?.(!isInvisible);
  }, [isInvisible, onVisibilityChange]);

  if (isInvisible) {
    return null;
  }

  const getPlatformText = () => {
    if (platform === 'osx') {
      return t('MacOSLocation');
    }
    if (platform === 'linux') {
      return t('LinuxLocation');
    }
    return t('WindowsLocation');
  };

  return (
    <div className={styles.Banner}>
      <div className={styles.BannerContent}>
        <span>
          {t('DownloadText')}{' '}
          <a
            href={getHubDownloadUrl(platform, architecture)}
            target='_blank'
            rel='noopener noreferrer'
          >
            {t('DownloadLink')}
          </a>{' '}
          {t('LaunchInstructions')} {getPlatformText()} {t('AlreadyInstalled')}
        </span>
        <button
          className={styles.DismissButton}
          onClick={handleDismiss}
          aria-label={t('Dismiss')}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default memo(DesktopAppBanner);
